import { Box, Button, CssBaseline, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { BACKGROUND_COLORS } from '@shared/ui';

import { useNavigate } from 'react-router-dom';

import { ILoginResponse, notificationStore, useUserStore } from 'hpacademy-data-authentication';

import LOGIN_BACKGROUND_IMAGE from '../../assets/images/login-background.jpg';
import { postRequest } from '@homework-studio/api-client';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const schema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

export default function SignIn() {
  const { user, setLoginSuccessResponse } = useUserStore();
  const { triggerNotification } = notificationStore();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (user?.accessToken) {
      navigate('/');
    }
  }, [user, navigate]);

  const onSubmit = async (payload: any) => {
    setLoading(true);
    setError('');
    try {
      const response: {
        data: ILoginResponse;
      } = await postRequest('/auth/login', payload);

      if (response) {
        const loginData: ILoginResponse = {
          userInfo: response.data.userInfo,
          accessToken: response.data.accessToken,
        };
        setLoginSuccessResponse(loginData);
        navigate('/');
      }
    } catch (err) {
      console.error('Login failed:', err);
      triggerNotification({
        open: true,
        type: 'error',
        msg: 'Invalid username or password',
      });
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `url(${LOGIN_BACKGROUND_IMAGE})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <CssBaseline />

      <Stack flexDirection={'row'} height={'100vh'}>
        <Box
          sx={{
            backgroundImage: `url(${LOGIN_BACKGROUND_IMAGE})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center end',
            height: '100%',
            width: {
              sm: '0',
              md: '60%',
              lg: '70%',
            },
          }}
        ></Box>

        <Stack
          position={'relative'}
          justifyContent={'space-around'}
          gap={2}
          sx={{
            background: '#ECEEF2',
            padding: '1rem',
            height: '100%',
            width: {
              sm: '100%',
              md: '40%',
              lg: '30%',
            },
          }}
        >
          <Box display={'flex'} justifyContent={'center'}>
            <img src={'/images/logo.png'} style={{ width: '250px' }} />
          </Box>
          <Box
            sx={{
              borderRadius: '1rem',
              background: '#FFFFFF',
              padding: '1rem 1rem 2rem 1rem',
            }}
          >
            <Typography component='h1' variant='h5' textAlign={'center'} marginBottom={4}>
              Login
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                label='Email or Phone'
                fullWidth
                margin='normal'
                {...register('username')}
                error={!!errors.username}
                helperText={errors.username?.message}
                disabled={loading}
              />

              <TextField
                label='Password'
                type='password'
                fullWidth
                margin='normal'
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
              />

              <Button
                type='submit'
                fullWidth
                variant='contained'
                sx={{
                  marginTop: 3,
                  minHeight: '3rem',
                  background: BACKGROUND_COLORS.MAIN,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
                disabled={loading}
              >
                {loading ? <Box className='loader' marginLeft={1}></Box> : <>Submit</>}
              </Button>
            </form>
          </Box>

          <Typography
            sx={{
              textAlign: 'center',
              marginTop: 4,
              color: '#8695AA',
            }}
          >
            Version: 1.0.0
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
